import { h, Fragment } from 'preact';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import { useState, useEffect, useRef, useMemo } from 'preact/hooks';
import Storefront from 'utils/storefront';
import { formatProduct } from 'utils/storefront/format-resource';
import TrapFocus from 'utils/trap-focus';
import Close from 'utils/icon/icon-close.svg';
import clsx from 'clsx';
import Image from 'utils/image';
import { Transition } from '@headlessui/react';
import { isMelanieLyne } from 'utils/helpers';
import useResponsive from 'utils/responsive';
import { getProductsByIds } from 'utils/storefront/queries';
import ShopTheLookOverlayProduct from './shop-the-look-overlay-product';
import ProductContext from '../product/product-context';

const ShopTheLookOverlay = () => {
  const storefront = Storefront.getInstance();
  const [productData, setProductData] = useState(null);
  const [products, setProducts] = useState([]);
  const [descriptionState, setDescriptionState] = useState(0);
  const descriptionContainer = useRef(null);
  const { isMobile } = useResponsive();

  const [isOpened, setIsOpened] = useState(false);

  const shopTheLookButtons = document.querySelectorAll(
    'a[href^="#look&look-product-id="]',
  );

  const shopTheLookOverlayProduct = {
    open: (id) => {
      const fetchData = async () => {
        setProductData(await getProductData(id));
      };
      fetchData();
    },
  };

  const handleShopTheLookOverlay = (e, hash = null) => {
    e.preventDefault();
    const targetHash = hash || e.currentTarget.hash;
    window.history.replaceState(
      null,
      '',
      `${window.location.href.split('#')[0]}${targetHash}`,
    );
    shopTheLookOverlayProduct.open(targetHash.split('look-product-id=')[1]);
  };

  const handleExternalLookOverlay = (e) => {
    window.history.replaceState(
      null,
      '',
      `${window.location.href.split('#')[0]}#look&look-product-id=${
        e.detail.id
      }`,
    );
    shopTheLookOverlayProduct.open(e.detail.id);
  };

  const handleClose = () => {
    window.history.replaceState(null, '', window.location.href.split('#')[0]);
    setProductData(null);
    setProducts([]);
    setIsOpened(false);
  };

  const handleClick = (e) => {
    const target = e.target.closest('a');
    if (
      target?.tagName.toLowerCase() === 'a' &&
      target.href.includes('look-product-id=')
    ) {
      e.preventDefault();
      e.stopPropagation();
      handleShopTheLookOverlay(e, target.hash);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClick);
    if (shopTheLookButtons)
      shopTheLookButtons.forEach((shopTheLookButton) => {
        shopTheLookButton.addEventListener('click', handleShopTheLookOverlay);
      });
    document.addEventListener('look-overlay:open', handleExternalLookOverlay);
    if (window.location.hash.includes('look-product-id')) {
      document.dispatchEvent(
        new CustomEvent('look-overlay:open', {
          detail: {
            id: `${window.location.hash}`.split('look-product-id=')[1],
          },
        }),
      );
    }
    return () => {
      if (shopTheLookButtons)
        shopTheLookButtons.forEach((shopTheLookButton) =>
          shopTheLookButton.removeEventListener(
            'click',
            handleShopTheLookOverlay,
          ),
        );
      document.removeEventListener('click', handleClick);
      document.removeEventListener(
        'look-overlay:open',
        handleExternalLookOverlay,
      );
    };
  }, []);

  const getProductData = async (queryID) => {
    const ids = [`gid://shopify/Product/${queryID}`];
    const response = await storefront.request({
      query: getProductsByIds,
      variables: {
        ids,
      },
    });

    if (!response.data) {
      // eslint-disable-next-line no-console
      console.error(
        'shop-the-look-overlay.js: Storefront API request failed',
        response,
      );
      return null;
    }

    const existingProducts = response.data.nodes.filter(
      (product) => product != null,
    );

    if (!existingProducts.length) {
      return null;
    }

    return {
      product: formatProduct(existingProducts[0], 'product'),
    };
  };

  const getLookProducts = async () => {
    if (!productData.product.look_products?.value) {
      return [];
    }
    const productQueryLinks = JSON.parse(
      productData.product.look_products.value,
    );
    const ids = productQueryLinks.map((link) => `${link}`);

    const response = await storefront.request({
      query: getProductsByIds,
      variables: {
        ids,
      },
    });

    if (!response.data) {
      return [];
    }

    const resultProducts = response.data.nodes
      .filter(product => !!product)
      .map((product) => {
      const objReturn = {};
      objReturn.product = formatProduct(product, 'product');
      return objReturn;
    });

    // return edgesToNodes(response.data.nodes, 'product');

    return resultProducts;
  };

  useEffect(() => {
    const initLookOverlay = () => {
      setIsOpened(true);

      getLookProducts().then((items) => {
        setProducts(items);
        setTimeout(() => {
          document.dispatchEvent(
            new CustomEvent('look-overlay:loaded', {
              detail: {
                id: productData.product.id,
              },
            }),
          );

          if (
            descriptionContainer.current &&
            descriptionContainer.current.scrollHeight >
              descriptionContainer.current.clientHeight
          ) {
            setDescriptionState(1);
          }
        }, 100);
      });
    };

    if (!productData) return;
    initLookOverlay();
  }, [productData]);

  let trapFocus = null;

  useEffect(() => {
    if (isOpened) {
      if (!trapFocus)
        trapFocus = new TrapFocus(
          document.querySelector('.look-overlay__content'),
        );
      document.body.classList.add('overflow-hidden');
      trapFocus.init(true);
    }

    return () => {
      document.body.classList.remove('overflow-hidden');
      if (trapFocus)
        trapFocus.destroy(
          document.querySelector(
            `[data-handle="${productData.product.handle}"]`,
          ),
        );
    };
  }, [isOpened, trapFocus]);

  const paginationConfig = useMemo(() => {
    if (!isMobile) {
      return {
        enabled: false,
      };
    }
    if (isMelanieLyne() && isMobile) {
      return {
        type: 'fraction',
        horizontalClass: '!w-auto !left-20 !bottom-16',
      };
    }

    return {
      clickable: true,
      bulletActiveClass: '!bg-black',
      bulletClass:
        'w-8 h-8 bg-white !opacity-100 inline-block mx-3 rounded-full',
      horizontalClass: '!w-auto !left-20 !bottom-16',
    };
  }, []);

  const description = useMemo(() => {
    if (descriptionState === 1) {
      return `${productData?.product?.descriptionHtml
        ?.replace(/(<([^>]+)>)/gi, '')
        .substring(0, 120)}...`;
    }
    return productData?.product?.descriptionHtml;
  }, [productData, descriptionState]);

  return (
    <>
      <div
        className={clsx(
          'shop-the-look-drawer look-overlay fixed flex flex-col  z-[1000] w-full top-0 bottom-0 right-0 justify-end md:w-[1094px] md:max-w-full bg-white duration-300',
          {
            'translate-x-0': isOpened,
            'translate-x-full transition-all': !isOpened,
          },
        )}
      >
        {!isOpened ? null : (
          <div
            className={clsx(
              'look-overlay__content absolute flex w-full justify-end z-50 top-0 bottom-0 right-0',
            )}
          >
            <div className="flex flex-col w-full md:max-w-507">
              <button
                className="look-overlay__button absolute w-24 h-24 top-24 right-24"
                aria-label={window.TMW.Strings.icons.close}
                title={window.TMW.Strings.icons.close}
                tabIndex="0"
                onClick={handleClose}
              >
                <Close />
              </button>
              <div className="flex justify-between h-80 p-24 items-center border-b border-b-grey-400">
                <div className="heading-3">
                  {window.TMW.Strings.general.shopTheLookOverlayTitle}
                </div>
              </div>
              <div className="overflow-auto hide-scrollbar">
                <div className="md:absolute md:inset-0 md:max-w-[calc(100%_-_507px)]">
                  <Swiper
                    modules={[Pagination]}
                    className="md:h-full md:!overflow-auto md:hide-scrollbar"
                    slidesPerView={1}
                    pagination={paginationConfig}
                    breakpoints={{
                      768: {
                        slidesPerView: 1,
                        allowTouchMove: false,
                        preventInteractionOnTransition: true,
                      },
                    }}
                    data-target="look-overlay-slider"
                  >
                    {productData.product.images.map((image, index) => (
                      <SwiperSlide
                        key={index}
                        className="md:!w-full md:!h-auto"
                      >
                        <Image
                          {...image}
                          key={index}
                          srcWidths={{
                            sm: 640,
                            md: 768,
                            lg: 1024,
                            xl: 1280,
                            '2xl': 1536,
                          }}
                          classes="w-full h-full object-cover"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
                <div className="h-flex flex-grow flex-col">
                  <div className="p-24">
                    <h3>{productData.product.title.replace('(Outfit)', '')}</h3>
                    {productData.product.descriptionHtml ? (
                      <>
                        <span
                          className={`body-3 text-subdued mt-8 ${
                            descriptionState !== 2 && descriptionState !== 1
                              ? 'line-clamp-2'
                              : ''
                          }`}
                          data-target="text-content"
                          ref={descriptionContainer}
                          dangerouslySetInnerHTML={{
                            __html: description,
                          }}
                        ></span>{' '}
                        {descriptionState === 1 && (
                          <button
                            className="body-3-underline inline hover:no-underline ml:mx-auto text-subdued"
                            onClick={() => setDescriptionState(2)}
                            dangerouslySetInnerHTML={{
                              __html: window.TMW.Strings.general.readMore,
                            }}
                          ></button>
                        )}
                        {descriptionState === 2 && (
                          <button
                            className="body-3-underline inline hover:no-underline ml:mx-auto text-subdued"
                            onClick={() => setDescriptionState(1)}
                            dangerouslySetInnerHTML={{
                              __html: window.TMW.Strings.general.readLess,
                            }}
                          ></button>
                        )}
                      </>
                    ) : null}
                  </div>
                  <div>
                    {products.map((product) => (
                      <ProductContext data={product} key={product.id}>
                        <ShopTheLookOverlayProduct />
                      </ProductContext>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Transition
        show={isOpened}
        as={Fragment}
        enter="transition-opacity ease-linear duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity ease-linear duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className="fixed inset-0 bg-black/20 z-50"
          aria-hidden="true"
          onClick={() => handleClose()}
        />
      </Transition>
    </>
  );
};

export default ShopTheLookOverlay;
